/**
 * C-09 得意先・現場／C-10 商品の**単票フォーム**（T-D8・05 §6.1）。
 *
 * **なぜ M1 に要るか**：これまでマスタを作る手段は CSV 投入だけで、画面には
 * 「CSV で取り込むか、1件ずつ登録してください」と書いてあるのに 1 件ずつ登録する導線が
 * 無かった（監査 masters#3）。パイロットは**3〜5社を同時にオンボード**する前提に変わった
 * ＝「1商品だけ足す」「掛率だけ直す」を CSV で往復させると立ち上げが回らない。
 *
 * 設計上の制約をそのまま形にしている：
 * - **表示番号（display_no）は新規のときだけ入力できる**＝`CustomerPatch`/`ProductPatch` に
 *   その項目が無い（04 §1＝内部PKと表示番号を分ける・表示番号は台帳の背番号で後から動かさない）。
 *   空で保存すると backend が採番する。
 * - **単位は API が返す値域から選ぶ**（`masters/meta` の units）＝画面に選択肢を書かない（FR-82）。
 * - 金額・掛率は**文字列のまま送る**（JS の number を経由すると丸めが混ざる＝04 §1 #5）。
 * - サーバに弾かれたら **field_errors を日本語の欄名で出す**（S4 レビュー #1 と同じ作法）。
 */

import {
  ApiError,
  console_,
  uploadProductImage,
  type Customer,
  type Product,
  type Site,
} from "@kenzai/api-client";
import { Card, ErrorNote, Spinner } from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { hintStyle, inputStyle, labelStyle, overlayStyle } from "./formStyles";

const FIELD_LABEL: Record<string, string> = {
  display_no: "番号",
  name: "名称",
  person: "担当者",
  fax_number: "FAX番号",
  kake_rate: "掛率",
  payment_terms: "支払条件",
  note: "備考",
  spec: "規格",
  unit: "単位",
  list_price: "定価",
  cost_price: "仕入値",
  category: "カテゴリ",
  reorder_point: "発注点",
  address: "住所",
  updated_at: "更新の競合",
};

/**
 * サーバ側の値域で弾かれたとき、**どの欄か**が分かる形に写す。
 *
 * `message` は backend が自分で書いたもの（日本語）と pydantic が出したもの（英語）が混ざる。
 * **英語のまま出さない**＝「定価：Input should be less than or equal to …」と見せても伝わらない。
 */
export function FieldErrors({ error }: { error: unknown }) {
  if (!(error instanceof ApiError) || error.fieldErrors.length === 0) return null;
  const hasJapanese = (s: string) => /[ぁ-んァ-ヶ一-龠]/.test(s);
  const items = [
    ...new Set(
      error.fieldErrors.map((fe) => {
        const label = FIELD_LABEL[fe.field] ?? fe.field;
        return hasJapanese(fe.message) ? `${label}：${fe.message}` : `${label} をご確認ください。`;
      }),
    ),
  ];
  return (
    <ul
      role="alert"
      data-testid="field-errors"
      style={{
        margin: "0 0 var(--kz-space-3)",
        padding: "var(--kz-space-3) var(--kz-space-3) var(--kz-space-3) var(--kz-space-5)",
        background: "var(--kz-status-danger-weak)",
        color: "var(--kz-status-danger)",
        border: "1px solid currentColor",
        borderRadius: "var(--kz-radius-sm)",
        fontSize: "var(--kz-font-size-sm)",
      }}
    >
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

/** 単票の入れもの。**背景クリックでは閉じない**（入力中に押して消えると打ち直しになる）。 */
function Modal({
  title,
  error,
  busy,
  canSave,
  saveTestId,
  onSave,
  onCancel,
  children,
}: {
  title: string;
  error: unknown;
  busy: boolean;
  canSave: boolean;
  saveTestId: string;
  onSave: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label={title}>
      {/* M1.7＝共有の Modal と同じ標高・同じ出現のしかたに揃える（`kz-modal-panel`＋一番深い影）。 */}
      <Card className="kz-modal-panel" style={{ width: "min(560px, 100%)", boxShadow: "var(--kz-shadow-lg)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-lg)" }}>{title}</h2>
        <div style={{ marginTop: "var(--kz-space-4)" }}>
          <ErrorNote error={error} />
          <FieldErrors error={error} />
        </div>
        <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>{children}</div>
        <div
          style={{
            display: "flex",
            gap: "var(--kz-space-2)",
            marginTop: "var(--kz-space-5)",
            justifyContent: "flex-end",
          }}
        >
          <button type="button" onClick={onCancel} className="kz-btn">
            やめる
          </button>
          <button
            type="button"
            data-testid={saveTestId}
            disabled={busy || !canSave}
            onClick={onSave}
            className="kz-btn kz-btn--primary"
          >
            {busy ? "保存しています…" : "保存する"}
          </button>
        </div>
      </Card>
    </div>
  );
}

/** 表示番号の欄。**編集時は読み取り専用**（PATCH に項目が無い＝送っても変わらない）。 */
function DisplayNoField({
  value,
  readOnly,
  onChange,
  testid,
  placeholder,
}: {
  value: string;
  readOnly: boolean;
  onChange: (v: string) => void;
  testid: string;
  placeholder: string;
}) {
  return (
    <label style={labelStyle}>
      番号
      <input
        data-testid={testid}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={40}
        style={{ ...inputStyle, background: readOnly ? "var(--kz-color-surface-sunken)" : inputStyle.background }}
      />
      <span style={hintStyle}>
        {readOnly
          ? "登録後は変更できません（台帳・CSV の突き合わせに使う番号のため）。"
          : "空のままにすると自動で採番します。"}
      </span>
    </label>
  );
}

/**
 * 小数の入力値の検査（**送るのは文字列のまま**＝ここは可否の判定だけに使う）。
 *
 * `scale`（小数点以下の桁数）は **DB の桁数と揃える**（0003_masters.py＝金額は NUMERIC(12,0)＝
 * 円の整数・掛率は NUMERIC(4,3)・発注点は NUMERIC(12,2)）。ここで見ないと、`620.5` と打った値が
 * PostgreSQL 側で `621` に丸められ、**誰にも知らされないまま違う金額が保存される**。
 */
export function decimalOk(
  raw: string,
  { min, max, exclusiveMin, scale }: { min?: number; max?: number; exclusiveMin?: boolean; scale: number },
) {
  const s = raw.trim();
  // 指数表記（1e3）や全角を弾く＝`Number()` は "1e3" を通してしまう
  const m = /^-?\d+(?:\.(\d+))?$/.exec(s);
  if (!m) return false;
  if ((m[1]?.length ?? 0) > scale) return false;
  const n = Number(s);
  if (!Number.isFinite(n)) return false;
  if (min !== undefined && (exclusiveMin ? n <= min : n < min)) return false;
  if (max !== undefined && n > max) return false;
  return true;
}

/** 金額の上限＝NUMERIC(12,0)（backend の `MONEY_MAX` と同じ値）。
 *  **scale だけ見て桁数を見ていなかった**ため、13桁を貼り付けると画面は素通しし
 *  DB の `numeric field overflow` で 500 になっていた（backend 側も 400 で弾くようにしたが、
 *  保存ボタンを押す前に理由を出すのが本筋）。 */
export const MONEY_MAX = 999_999_999_999;
export const REORDER_POINT_MAX = 9_999_999_999.99;
export const moneyOk = (raw: string) => decimalOk(raw, { min: 0, max: MONEY_MAX, scale: 0 });

const trimOrNull = (v: string) => (v.trim() === "" ? null : v.trim());

// ---------------------------------------------------------------------------
// 得意先（C-09）
// ---------------------------------------------------------------------------

export function CustomerForm({
  initial,
  onDone,
  onCancel,
}: {
  initial?: Customer;
  onDone: (c: Customer) => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [displayNo, setDisplayNo] = useState(initial?.display_no ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [person, setPerson] = useState(initial?.person ?? "");
  const [fax, setFax] = useState(initial?.fax_number ?? "");
  const [kake, setKake] = useState(initial?.kake_rate ?? "");
  const [terms, setTerms] = useState(initial?.payment_terms ?? "");
  // 既定は false ＝ CSV 投入・API の既定と同じ（画面から作った得意先だけ印が付く、を作らない）
  const [lineEnabled, setLineEnabled] = useState(initial?.line_enabled ?? false);
  const [note, setNote] = useState(initial?.note ?? "");

  const save = useMutation({
    mutationFn: () => {
      const values = {
        name: name.trim(),
        person: trimOrNull(person),
        fax_number: trimOrNull(fax),
        kake_rate: trimOrNull(kake),
        payment_terms: trimOrNull(terms),
        line_enabled: lineEnabled,
        note: trimOrNull(note),
      };
      return initial
        ? console_.patchCustomer(initial.id, { ...values, updated_at: initial.updated_at })
        : console_.createCustomer({ ...values, display_no: trimOrNull(displayNo) });
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["customer", c.id] });
      onDone(c);
    },
  });

  // 掛率は backend が 0 < r ≤ 2（`gt=0, le=2`）・DB が NUMERIC(4,3)＝**ここも同じ範囲・桁数**で見る。
  // 画面だけ緩いと「88」（％のつもり）が 422 になって理由が分からない。
  const kakeOk = kake.trim() === "" || decimalOk(kake, { min: 0, max: 2, exclusiveMin: true, scale: 3 });

  return (
    <Modal
      title={initial ? "取引先を編集" : "取引先を追加"}
      error={save.error}
      busy={save.isPending}
      canSave={name.trim() !== "" && kakeOk}
      saveTestId="customer-save"
      onSave={() => save.mutate()}
      onCancel={onCancel}
    >
      <DisplayNoField
        testid="customer-display-no"
        value={displayNo}
        readOnly={!!initial}
        onChange={setDisplayNo}
        placeholder="C-001"
      />
      <label style={labelStyle}>
        取引先名
        <input
          data-testid="customer-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="田中工務店"
          maxLength={200}
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        担当者
        <input
          data-testid="customer-person"
          value={person}
          onChange={(e) => setPerson(e.target.value)}
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        FAX番号
        <input
          data-testid="customer-fax"
          value={fax}
          onChange={(e) => setFax(e.target.value)}
          placeholder="04-1234-5678"
          style={inputStyle}
        />
        <span style={hintStyle}>FAX で届いた注文を、この取引先として突き合わせるときに使います。</span>
      </label>
      <label style={labelStyle}>
        掛率
        <input
          data-testid="customer-kake"
          value={kake}
          onChange={(e) => setKake(e.target.value)}
          placeholder="0.88"
          inputMode="decimal"
          style={inputStyle}
        />
        <span style={hintStyle}>
          定価に掛ける数（0.88 なら定価の88％）。空にすると定価のまま。
          {!kakeOk && <strong>（0 より大きく 2 以下・小数第3位までで入力してください）</strong>}
        </span>
      </label>
      <label style={labelStyle}>
        支払条件
        <input
          data-testid="customer-terms"
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
          placeholder="月末締め翌月末払い"
          style={inputStyle}
        />
      </label>
      <div style={labelStyle}>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-2)" }}>
          <input
            type="checkbox"
            data-testid="customer-app-order"
            checked={lineEnabled}
            onChange={(e) => setLineEnabled(e.target.checked)}
            style={{ width: 18, height: 18 }}
          />
          アプリ発注あり
        </label>
        {/* この印は一覧で見分けるためのもの＝**外しても注文は止まらない**。
            止められると誤解されると「外したのに注文が来る」になる（接続の解除が要る）。 */}
        <span style={hintStyle}>一覧で見分けるための印です。外しても取引先アプリからの注文は止まりません。</span>
      </div>
      <label style={labelStyle}>
        備考
        <input
          data-testid="customer-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={inputStyle}
        />
        <span style={hintStyle}>社内向けの覚え書き。取引先には出ません。</span>
      </label>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 現場（C-09 の詳細内・FR-12）
// ---------------------------------------------------------------------------

export function SiteForm({
  customerId,
  initial,
  onDone,
  onCancel,
}: {
  customerId: number;
  initial?: Site;
  onDone: () => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(initial?.name ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");

  const save = useMutation({
    mutationFn: () => {
      const values = { name: name.trim(), address: trimOrNull(address) };
      return initial
        ? console_.patchSite(initial.id, { ...values, updated_at: initial.updated_at })
        : console_.createSite(customerId, values);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites", customerId] });
      onDone();
    },
  });

  return (
    <Modal
      title={initial ? "現場を編集" : "現場を追加"}
      error={save.error}
      busy={save.isPending}
      canSave={name.trim() !== ""}
      saveTestId="site-save"
      onSave={() => save.mutate()}
      onCancel={onCancel}
    >
      <label style={labelStyle}>
        現場名
        <input
          data-testid="site-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="若葉町 新築工事"
          maxLength={200}
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        納品先住所
        <input
          data-testid="site-address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          style={inputStyle}
        />
        <span style={hintStyle}>取引先が注文するとき、この現場を選ぶと納品先になります。</span>
      </label>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 商品（C-10）
// ---------------------------------------------------------------------------

/**
 * 商品写真（FR-119・**D-64**）＝1商品1枚。**登録済みの商品にだけ**付けられる。
 *
 * 新規作成のフォームでは出さない＝写真は商品IDが決まってからでないと保存先が決まらない
 * （保存先は `tenants/{tenant}/products/{product_id}/…`）。作ってから編集で付けてもらう。
 *
 * 形式の判定は **backend が中身（先頭バイト）で**やる。ここで拡張子を見て安心しない＝
 * `.jpg` を名乗る HTML は、名前だけ見ていると通ってしまう。
 */
function ProductImageField({
  product,
  onChanged,
}: {
  product: Product;
  /** 写真を張り替えると **`updated_at` が進む**（products の BEFORE UPDATE トリガ）。
   *  親に返さないと、同じモーダルで続けて「保存」を押したときに自分の操作が原因で
   *  409「他の担当者が先に更新しました」＝**事実と違う理由**で拒否される。 */
  onChanged: (p: Product) => void;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState<string | null>(product.image_url);
  const [error, setError] = useState<unknown>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["products"] });
    qc.invalidateQueries({ queryKey: ["product", product.id] });
  };

  const upload = useMutation({
    mutationFn: (file: File) => uploadProductImage(product.id, file),
    onSuccess: (p) => {
      setError(null);
      setUrl(p.image_url);
      onChanged(p);
      invalidate();
    },
    onError: setError,
  });
  const remove = useMutation({
    mutationFn: () => console_.deleteProductImage(product.id),
    onSuccess: (p) => {
      setError(null);
      setUrl(p.image_url);
      onChanged(p);
      invalidate();
    },
    onError: setError,
  });

  const busy = upload.isPending || remove.isPending;

  return (
    <div style={labelStyle}>
      写真
      <div style={{ display: "flex", gap: "var(--kz-space-3)", alignItems: "flex-start" }}>
        <div
          data-testid="product-image-preview"
          style={{
            width: 96,
            height: 96,
            flex: "none",
            borderRadius: "var(--kz-radius-sm)",
            border: "1px solid var(--kz-color-border)",
            background: "var(--kz-color-surface-sunken)",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-ink-muted)",
          }}
        >
          {url ? (
            <img
              src={url}
              alt={`${product.name} の写真`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            "なし"
          )}
        </div>
        <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
          <label className="kz-btn" style={{ opacity: busy ? 0.5 : 1 }}>
            {upload.isPending ? "アップロードしています…" : url ? "写真を差し替える" : "写真を選ぶ"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              data-testid="product-image-file"
              disabled={busy}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = ""; // 同じファイルを選び直せるようにする
                if (file) upload.mutate(file);
              }}
            />
          </label>
          {url && (
            <button
              type="button"
              data-testid="product-image-remove"
              disabled={busy}
              onClick={() => remove.mutate()}
              className="kz-btn"
            >
              写真を外す
            </button>
          )}
          <span style={hintStyle}>
            取引先の注文画面に出ます。JPEG・PNG・WebP／5MB まで。1商品に1枚です。
          </span>
        </div>
      </div>
      <ErrorNote error={error} />
    </div>
  );
}

export function ProductForm({
  initial,
  onDone,
  onCancel,
}: {
  initial?: Product;
  onDone: (p: Product) => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  // 単位・カテゴリの選択肢は API から（画面にハードコードしない＝FR-82）
  const meta = useQuery({ queryKey: ["masters-meta"], queryFn: () => console_.mastersMeta() });
  const [displayNo, setDisplayNo] = useState(initial?.display_no ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [spec, setSpec] = useState(initial?.spec ?? "");
  const [unit, setUnit] = useState(initial?.unit ?? "");
  const [listPrice, setListPrice] = useState(initial?.list_price ?? "");
  const [costPrice, setCostPrice] = useState(initial?.cost_price ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [reorderPoint, setReorderPoint] = useState(initial?.reorder_point ?? "");
  // 楽観ロックの基準（FR-81）。**写真を張り替えると進む**ので state で持ち、その都度追いつかせる
  // （`initial.updated_at` に固定すると、自分の写真アップが原因で保存が 409 になる）。
  const [lockedAt, setLockedAt] = useState(initial?.updated_at);

  const save = useMutation({
    mutationFn: () => {
      const values = {
        name: name.trim(),
        spec: trimOrNull(spec),
        unit,
        list_price: listPrice.trim(),
        cost_price: trimOrNull(costPrice),
        category: trimOrNull(category),
        reorder_point: trimOrNull(reorderPoint),
      };
      return initial
        ? console_.patchProduct(initial.id, { ...values, updated_at: lockedAt })
        : console_.createProduct({ ...values, display_no: trimOrNull(displayNo) });
    },
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["products"] });
      // ★ `["products"]`（一覧）は `["product", id]`（単品）に**マッチしない**。
      //   個別単価の画面は単品キャッシュ（staleTime 60秒）から定価を出しているので、
      //   ここを落とさないと「定価を直したのに個別単価の画面は最大60秒古いまま」になる。
      qc.invalidateQueries({ queryKey: ["product", p.id] });
      qc.invalidateQueries({ queryKey: ["masters-meta"] }); // 新しいカテゴリは選択肢に増える
      onDone(p);
    },
  });

  const units = meta.data?.units ?? [];
  // 金額は NUMERIC(12,0)＝**円の整数・12桁まで**。小数は PostgreSQL 側で黙って丸められ、
  // 13桁は `numeric field overflow` で落ちる＝どちらもここで止める
  const priceOk = moneyOk(listPrice);
  const costOk = costPrice.trim() === "" || moneyOk(costPrice);
  const reorderOk =
    reorderPoint.trim() === "" || decimalOk(reorderPoint, { min: 0, max: REORDER_POINT_MAX, scale: 2 });

  return (
    <Modal
      title={initial ? "商品を編集" : "商品を追加"}
      error={save.error ?? meta.error}
      busy={save.isPending}
      canSave={name.trim() !== "" && unit !== "" && priceOk && costOk && reorderOk}
      saveTestId="product-save"
      onSave={() => save.mutate()}
      onCancel={onCancel}
    >
      <DisplayNoField
        testid="product-display-no"
        value={displayNo}
        readOnly={!!initial}
        onChange={setDisplayNo}
        placeholder="P-001"
      />
      <label style={labelStyle}>
        商品名
        <input
          data-testid="product-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="石膏ボード 12.5mm"
          maxLength={200}
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        規格
        <input
          data-testid="product-spec"
          value={spec}
          onChange={(e) => setSpec(e.target.value)}
          placeholder="3×6判"
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        単位
        {meta.isLoading ? (
          <Spinner />
        ) : (
          <select className="kz-select"
            data-testid="product-unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            style={inputStyle}
          >
            <option value="">選んでください</option>
            {units.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        )}
      </label>
      <label style={labelStyle}>
        定価
        <input
          data-testid="product-price"
          value={listPrice}
          onChange={(e) => setListPrice(e.target.value)}
          placeholder="620"
          inputMode="decimal"
          style={inputStyle}
        />
        <span style={hintStyle}>
          税抜の単価。取引先ごとの掛率・個別単価はここから決まります。
          {!priceOk && listPrice.trim() !== "" && <strong>（0 以上・12桁までの円・整数で入力してください）</strong>}
        </span>
      </label>
      <label style={labelStyle}>
        仕入値
        <input
          data-testid="product-cost"
          value={costPrice}
          onChange={(e) => setCostPrice(e.target.value)}
          inputMode="decimal"
          style={inputStyle}
        />
        <span style={hintStyle}>
          社内向け。取引先には出ません。
          {!costOk && <strong>（0 以上・12桁までの円・整数で入力してください）</strong>}
        </span>
      </label>
      <label style={labelStyle}>
        カテゴリ
        <input
          data-testid="product-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          list="kz-product-categories"
          placeholder="ボード"
          style={inputStyle}
        />
        <datalist id="kz-product-categories">
          {(meta.data?.categories ?? []).map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <span style={hintStyle}>一覧の絞り込みに使います。既存のカテゴリは入力すると候補に出ます。</span>
      </label>
      <label style={labelStyle}>
        発注点
        <input
          data-testid="product-reorder"
          value={reorderPoint}
          onChange={(e) => setReorderPoint(e.target.value)}
          inputMode="decimal"
          style={inputStyle}
        />
        <span style={hintStyle}>
          在庫がこの数を下回ったら知らせます（在庫の機能は M2 で提供）。
          {!reorderOk && <strong>（0 以上・小数第2位までで入力してください）</strong>}
        </span>
      </label>
      {initial ? (
        <ProductImageField product={initial} onChanged={(p) => setLockedAt(p.updated_at)} />
      ) : (
        <span style={hintStyle}>写真は、登録したあとに「編集」から付けられます。</span>
      )}
    </Modal>
  );
}
